package info.rezident.app.pages.recommendations

import info.rezident.app.core.AppTracking
import info.rezident.app.core.LocalStorage
import info.rezident.app.core.Logger
import info.rezident.app.core.emitPurchaseEvent
import info.rezident.app.data.auth.AuthRepository
import info.rezident.app.data.payments.StripeCatalogRepository
import info.rezident.app.data.quiz.RecommendationItem
import info.rezident.app.data.quiz.RecommendationsResult
import info.rezident.app.data.quiz.UserQuizRepository
import info.rezident.app.data.user.UserRepository
import info.rezident.app.data.user.VariantsRepository
import info.rezident.app.domain.GenerateProConsExplanationsUseCase
import info.rezident.app.ui.components.widgets.CheckoutHelper
import kotlinx.coroutines.*
import kotlinx.coroutines.flow.*
import kotlinx.coroutines.sync.Mutex
import kotlinx.coroutines.sync.withLock
import kotlin.random.Random

/**
 * ViewModel for the Recommendations page that handles quiz-specific recommendations
 * and provides a single state flow for the UI to observe. Requires a valid quizId.
 */
class RecommendationsViewModel {
    private val scope = CoroutineScope(Dispatchers.Default)

    private val _state = MutableStateFlow(RecommendationsState())
    val state: StateFlow<RecommendationsState> = _state.asStateFlow()

    private val _effects = MutableSharedFlow<RecommendationsEffect>()
    val effects: SharedFlow<RecommendationsEffect> = _effects.asSharedFlow()

    // Add quiz ID state
    private var currentQuizId: String? = null

    // Guard and mutex to avoid duplicate confetti emissions
    private var hasEmittedConfetti: Boolean = false
    private val confettiMutex: Mutex = Mutex()

    // Debounced feedback save
    private var feedbackSaveJob: Job? = null

    // A/B variant (Remote Config `recommendations_sorted`), resolved once so the header and the cards always agree
    private val recommendationsSorted: Deferred<Boolean> = scope.async { VariantsRepository.recommendationsSorted() }
    private var hasTrackedVariantExposure: Boolean = false

    init {
        observeAuthState()
        resolveRecommendationsVariant()
    }

    /**
     * Initialize the page with a valid quizId
     */
    fun initializeWithQuizId(quizId: String) {
        // Store quiz ID for specific quiz recommendations
        currentQuizId = quizId

        // Track page open with quizId
        AppTracking.logEvent(
            AppTracking.EVENT_RECOMMENDATIONS_PAGE_OPEN,
            mapOf(
                AppTracking.PARAM_TEMPLATE_ID to 1,
                "quiz_id" to quizId
            )
        )

        // Validate the provided quizId
        scope.launch {
            validateQuizId(quizId)
        }
    }

    /**
     * Start payment listener for the current quiz using UserQuizRepository
     */
    private fun startPaymentListenerForQuiz(uid: String, quizId: String) {
        scope.launch {
            try {
                val (success, paymentInfo) = UserQuizRepository.waitForPaymentSuccess(uid, quizId)
                if (success) {
                    // Normal path: quiz is already unlocked by UserQuizRepository.waitForPaymentSuccess
                    Logger.log("Payment success: quiz $quizId unlocked")

                    // Emit purchase analytics event
                    emitPurchaseEvent(
                        quizId,
                        paymentId = paymentInfo?.paymentId,
                        amountMinorUnits = paymentInfo?.amountMinorUnits,
                        currencyCode = paymentInfo?.currencyCode,
                    )

                    // Update state to reflect unlocked access
                    _state.update {
                        it.copy(
                            isUnlocked = true,
                            paymentError = null
                        )
                    }
                    Logger.log("UI updated to show unlocked recommendations for quiz: $quizId")

                    // Try showing the celebration exactly once for this quiz
                    tryEmitUnlockConfetti(quizId)

                    // CRITICAL: Trigger pros/cons generation now that quiz is unlocked
                    // Recommendations are already loaded, we just need to generate explanations
                    generateProConsExplanationsIfNeeded(quizId)
                }
            } catch (e: Exception) {
                Logger.error("Error waiting for payment success: ${e.message}")
            }
        }
    }

    /**
     * Validate if the provided quizId exists and belongs to the current user.
     */
    private suspend fun validateQuizId(quizId: String) {
        try {
            // Ensure user is authenticated (anonymous is OK)
            // Will retry when auth state changes
            AuthRepository.firebaseUser.firstOrNull() ?: return

            val quizExists = UserQuizRepository.quizDocumentExistsInCurrentUserDoc(quizId)
            Logger.log("Quiz exists under current user: $quizExists")

            if (!quizExists) {
                // Quiz not found under current user
                _state.update {
                    it.copy(
                        pageError = RecommendationsPageError.QuizNotFound,
                        isLoading = false
                    )
                }
                AppTracking.logErrorDisplayedEvent(
                    location = "recommendations_validate_quiz",
                    errorType = "quiz_not_found",
                    quizId = quizId
                )
                return
            }
        } catch (e: Exception) {
            Logger.error("Error validating quiz: ${e.message}")
            _state.update {
                it.copy(
                    pageError = RecommendationsPageError.QuizNotFound,
                    isLoading = false
                )
            }
            AppTracking.logErrorDisplayedEvent(
                location = "recommendations_validate_quiz",
                errorType = "quiz_validation_exception",
                quizId = quizId,
                params = mapOf(
                    "message" to (e.message ?: "unknown")
                )
            )
        }
    }

    /**
     * Observe authentication state changes and set up payment listeners
     */
    private fun observeAuthState() {
        scope.launch {
            AuthRepository.firebaseUser.collectLatest { user ->
                if (user != null) {
                    // Update anonymous status
                    _state.update { it.copy(isAnonymousUser = user.isAnonymous, isNotSignedIn = false) }

                    // Load user's price variant and resolve price
                    loadUserPrice()

                    // Load recommendations
                    loadRecommendations()

                    // Load existing feedback for the current quiz
                    currentQuizId?.let { quizId ->
                        loadFeedback(quizId)
                    }
                } else {
                    // User is not authenticated yet, keep loading state
                    // Note: Payment listeners are automatically cancelled when coroutines are cancelled
                    _state.update {
                        it.copy(
                            isLoading = true,
                            pageError = null,
                            isNotSignedIn = true
                        )
                    }
                }
            }
        }
    }

    /**
     * Load recommendations for the current quiz
     */
    private fun loadRecommendations() {
        scope.launch {
            try {
                currentQuizId?.let { quizId ->
                    loadRecommendationsForQuiz(quizId)
                } ?: run {
                    _state.update {
                        it.copy(
                            pageError = RecommendationsPageError.QuizNotFound,
                            isLoading = false
                        )
                    }
                    AppTracking.logErrorDisplayedEvent(
                        location = "recommendations_load",
                        errorType = "missing_quiz_id",
                        quizId = currentQuizId
                    )
                }
            } catch (e: Exception) {
                _state.update {
                    it.copy(
                        pageError = RecommendationsPageError.RecommendationsNotAvailable,
                        isLoading = false
                    )
                }
                AppTracking.logErrorDisplayedEvent(
                    location = "recommendations_load",
                    errorType = "recommendations_load_exception",
                    quizId = currentQuizId,
                    params = mapOf(
                        "message" to (e.message ?: "unknown")
                    )
                )
            }
        }
    }

    /**
     * Load recommendations for a specific quiz document
     */
    private suspend fun loadRecommendationsForQuiz(quizId: String) {
        try {
            // One read of the quiz document gives the template version, unlock state, saved
            // recommendations and locked ranks. The answers aren't downloaded: only whether there
            // are any is checked (in parallel), and they are fetched later only if the
            // recommendations still have to be generated.
            val (quiz, hasAnswers) = coroutineScope {
                val quizDeferred = async { retryOnceIfNull { UserQuizRepository.getQuizWithoutAnswers(quizId) } }
                val hasAnswersDeferred = async { UserQuizRepository.quizHasAnswers(quizId) }
                quizDeferred.await() to hasAnswersDeferred.await()
            }

            if (quiz == null) {
                // Missing under the current user (or unreadable even after a retry)
                _state.update {
                    it.copy(
                        pageError = RecommendationsPageError.QuizNotFound,
                        isLoading = false
                    )
                }
                AppTracking.logErrorDisplayedEvent(
                    location = "recommendations_load_for_quiz",
                    errorType = "quiz_not_found",
                    quizId = quizId
                )
                return
            }

            // Update the UI flag about answers existence for this quiz. It only turns on, so a
            // reload that fails to see the answers doesn't hide the section again.
            if (hasAnswers) {
                _state.update { it.copy(hasAnswers = true) }
            }

            // Check quiz template version
            if (quiz.templateId < 2) {
                // Old quiz version - show upgrade message
                _state.update {
                    it.copy(
                        pageError = RecommendationsPageError.OldQuizVersion,
                        isLoading = false
                    )
                }
                AppTracking.logErrorDisplayedEvent(
                    location = "recommendations_load_for_quiz",
                    errorType = "old_quiz_version",
                    quizId = quizId,
                    params = mapOf(
                        "template_id" to quiz.templateId
                    )
                )
                return
            }

            // Check the unlock state from the quiz document
            val isUnlocked = quiz.isUnlocked
            Logger.log("Quiz unlock state: $isUnlocked")

            // Update isUnlocked state based on unlock status
            _state.update { it.copy(isUnlocked = isUnlocked) }

            // Start payment listener separately if needed
            if (!isUnlocked) {
                AuthRepository.getCurrentUser()?.let { user ->
                    startPaymentListenerForQuiz(user.uid, quizId)
                }
            }

            // Use the existing recommendations from cloud function, if any
            var result = quiz.recommendations

            // Generate via Cloud Function if not found
            if (result == null) {
                val startMs = js("Date.now()") as Double
                AppTracking.logEvent(
                    AppTracking.EVENT_RECOMMENDATIONS_GENERATE_START,
                    mapOf(
                        AppTracking.PARAM_TEMPLATE_ID to 1,
                        "method" to "cloud_function"
                    )
                )

                result = generateRecommendationsViaCloudFunction(quizId)
                val durationMs = (js("Date.now()") as Double) - startMs

                if (result != null) {
                    // Save Cloud Function results to Firestore
                    UserQuizRepository.setRecommendationsForQuiz(quizId, result)
                }
                
                AppTracking.logEvent(
                    AppTracking.EVENT_RECOMMENDATIONS_GENERATE_RESULT,
                    mapOf(
                        AppTracking.PARAM_TEMPLATE_ID to 1,
                        "success" to (result != null),
                        "duration_ms" to durationMs,
                        "method" to "cloud_function"
                    )
                )
            }

            if (result != null) {
                val sorted = recommendationsSorted.await()

                // Variant A: ranked order, as before. Variant B: random display order, seeded by quizId so it stays
                // the same on every visit. Ranks are sorted first so the result doesn't depend on the stored array order.
                val ranks = result.recommendations.map { it.rank }.sorted()
                val recommendationOrder = if (sorted) {
                    ranks.sortedDescending()
                } else {
                    ranks.shuffled(Random(quizId.hashCode()))
                }

                // Use the saved locked recommendation ranks, or generate them
                val lockedRanks = resolveLockedRanks(
                    quizId = quizId,
                    savedRanks = quiz.lockedRecommendationRanks,
                    recommendations = result.recommendations,
                    firstDisplayedRank = recommendationOrder.firstOrNull(),
                    sorted = sorted
                )

                _state.update {
                    it.copy(
                        recommendations = result.recommendations,
                        incompatibleRecommendations = result.incompatibleRecommendations,
                        isLoading = false,
                        isUnlocked = isUnlocked,
                        pageError = null,
                        recommendationsSorted = sorted,
                        recommendationOrder = recommendationOrder,
                        lockedRecommendationRanks = lockedRanks
                    )
                }

                trackRecommendationsVariantExposure(sorted)

                if (isUnlocked) {
                    // In case of direct load after redirect, also try to emit confetti
                    tryEmitUnlockConfetti(quizId)

                    // Generate pros/cons explanations if needed
                    generateProConsExplanationsIfNeeded(quizId)
                }
            } else {
                _state.update {
                    it.copy(
                        pageError = RecommendationsPageError.RecommendationsNotAvailable,
                        isLoading = false
                    )
                }
                AppTracking.logErrorDisplayedEvent(
                    location = "recommendations_generate",
                    errorType = "recommendations_generation_failed",
                    quizId = quizId
                )
            }
        } catch (e: Exception) {
            _state.update {
                it.copy(
                    pageError = RecommendationsPageError.RecommendationsNotAvailable,
                    isLoading = false
                )
            }
            AppTracking.logErrorDisplayedEvent(
                location = "recommendations_load_for_quiz",
                errorType = "recommendations_load_exception",
                quizId = quizId,
                params = mapOf(
                    "message" to (e.message ?: "unknown")
                )
            )
        }
    }

    /**
     * Generate recommendations for a specific quiz document using Cloud Function
     */
    private suspend fun generateRecommendationsViaCloudFunction(quizId: String): RecommendationsResult? =
        try {
            // Fetch the quiz with its answers (the only place that needs them)
            val quiz = retryOnceIfNull { UserQuizRepository.getCompletedQuizWithAnswers(quizId) }

            if (quiz != null && quiz.answers.isNotEmpty()) {
                Logger.log("Calling Cloud Function to generate recommendations for quiz $quizId")

                // Call Cloud Function with quiz answers
                val result = info.rezident.app.data.CloudFunctionsRepository.computeMatchScores(quiz.answers)

                if (result != null) {
                    Logger.log("Cloud Function returned ${result.recommendations.size} recommendations")
                } else {
                    Logger.error("Cloud Function returned null for quiz $quizId")
                }

                result
            } else {
                Logger.error("Quiz $quizId not found or has no answers")
                null
            }
        } catch (e: Exception) {
            Logger.error("Error calling Cloud Function for quiz $quizId: ${e.message}")
            null
        }


    /**
     * Run [block] and, if it returns null (the repository returns null both for a missing document
     * and for a failed read), run it once more after a short delay so a single transient failure
     * doesn't end up as an error page.
     */
    private suspend fun <T : Any> retryOnceIfNull(block: suspend () -> T?): T? =
        block() ?: run {
            delay(RETRY_DELAY_MS)
            block()
        }

    /**
     * Load user's price variant and resolve the corresponding Stripe price.
     */
    private fun loadUserPrice() {
        scope.launch {
            try {
                Logger.log("[RecommendationsVM] loadUserPrice started")
                val userVariant = UserRepository.getUserPriceVariant()
                Logger.log("[RecommendationsVM] User variant: $userVariant")
                val stripePrice = StripeCatalogRepository.getActivePriceForVariant(userVariant, "one_time")
                Logger.log("[RecommendationsVM] Stripe price: ${stripePrice?.id}")

                if (stripePrice != null) {
                    // Format price text (unitAmount is in minor units, so divide by 100)
                    val stripePriceAmount = stripePrice.unitAmount / 100.0
                    val currencyText = stripePrice.currency.uppercase()

                    // Discounted price (shown prominently) = Stripe price as-is
                    val discountedText = ((stripePriceAmount).asDynamic().toFixed(2) as String) + " " + currencyText

                    // Full price (strikethrough) = Stripe price × 2
                    val fullPriceAmount = stripePriceAmount * 2
                    val fullPriceText = ((fullPriceAmount).asDynamic().toFixed(2) as String) + " " + currencyText
                    
                    val discountText = "-50%"

                    _state.update {
                        it.copy(
                            selectedPriceId = stripePrice.id,
                            selectedPriceText = discountedText,
                            selectedPriceInitialText = fullPriceText,
                            selectedPriceDiscountText = discountText
                        )
                    }
                    Logger.log("Loaded price for variant $userVariant: $discountedText (ID: ${stripePrice.id}) | initial=$fullPriceText, discount=$discountText")
                } else {
                    Logger.warn("No price found for user variant: $userVariant")
                }
            } catch (e: Exception) {
                Logger.error("Error loading user price: ${e.message}")
            }
        }
    }

    /**
     * Try to emit a one-time confetti effect for a given quizId.
     * Uses LocalStorage to persist a set of celebrated quiz IDs and a local guard to prevent duplicates.
     */
    private suspend fun tryEmitUnlockConfetti(quizId: String) {
        confettiMutex.withLock {
            if (hasEmittedConfetti) return

            val stored: String? = LocalStorage.get(LocalStorage.KEY_CELEBRATED_QUIZ_IDS)
            val ids: MutableSet<String> = if (!stored.isNullOrBlank()) stored.split(',').filter { it.isNotBlank() }
                .toMutableSet() else mutableSetOf()

            if (ids.contains(quizId)) return

            // Persist first to be idempotent across refreshes and concurrent listeners
            ids.add(quizId)
            LocalStorage.save(LocalStorage.KEY_CELEBRATED_QUIZ_IDS, ids.joinToString(","))

            hasEmittedConfetti = true

            state.firstOrNull {
                !it.isLoading
            }?.also {
                _effects.emit(RecommendationsEffect.ShowConfetti)
            }
        }
    }

    /**
     * Update feedback for the current quiz with debounced save
     */
    fun updateFeedback(newFeedback: RecommendationsFeedback) {
        _state.update { it.copy(feedback = newFeedback) }

        // Cancel previous save job if it exists
        feedbackSaveJob?.cancel()

        // Start new debounced save job with 1.5 second delay
        currentQuizId?.let { quizId ->
            feedbackSaveJob = scope.launch {
                delay(1500) // 1.5 seconds delay
                saveFeedback(quizId, newFeedback)
            }
        }
    }

    /**
     * Save feedback to Firestore for the current quiz
     */
    private suspend fun saveFeedback(quizId: String, feedback: RecommendationsFeedback) {
        try {
            val success = UserQuizRepository.saveFeedbackForQuiz(quizId, feedback)
            if (success) {
                Logger.log("Feedback saved successfully for quiz $quizId")
                // Track feedback update
                AppTracking.logEvent(
                    AppTracking.EVENT_FEEDBACK_UPDATE,
                    mapOf(
                        AppTracking.PARAM_TEMPLATE_ID to 1,
                        "useful_score" to (feedback.usefulScore ?: 0),
                        "expectation_score" to (feedback.expectationScore ?: 0),
                        "has_comment" to (feedback.comment?.isNotBlank() == true)
                    )
                )
            } else {
                Logger.error("Failed to save feedback for quiz $quizId")
            }
        } catch (e: Exception) {
            Logger.error("Error saving feedback for quiz $quizId: ${e.message}")
        }
    }

    /**
     * Load existing feedback for the current quiz
     */
    private fun loadFeedback(quizId: String) {
        scope.launch {
            try {
                val feedback = UserQuizRepository.getFeedbackForQuiz(quizId)
                if (feedback != null) {
                    _state.update { it.copy(feedback = feedback) }
                }
            } catch (e: Exception) {
                Logger.error("Error loading feedback for quiz $quizId: ${e.message}")
            }
        }
    }

    /**
     * Start checkout process using the shared CheckoutHelper
     */
    fun startCheckout() {
        Logger.log("[RecommendationsVM] startCheckout called")
        // Clear any previous payment error
        _state.update { it.copy(paymentError = null) }
        
        scope.launch {
            Logger.log("[RecommendationsVM] Calling CheckoutHelper.startCheckout")
            CheckoutHelper.startCheckout(
                quizId = currentQuizId,
                selectedPriceId = _state.value.selectedPriceId,
                selectedPriceText = _state.value.selectedPriceText,
                onCheckoutStateChange = { isCreating ->
                    Logger.log("[RecommendationsVM] Checkout state changed: isCreating=$isCreating")
                    _state.update { it.copy(isCreatingCheckout = isCreating) }
                },
                onNavigate = { url ->
                    Logger.log("[RecommendationsVM] onNavigate called with URL: $url")
                    scope.launch {
                        _effects.emit(RecommendationsEffect.NavigateTo(url))
                    }
                    Logger.log("[RecommendationsVM] NavigateTo effect emitted")
                },
                onError = { errorMessage ->
                    Logger.log("[RecommendationsVM] Payment error: $errorMessage")
                    _state.update {
                        it.copy(
                            paymentError = PaymentError(message = errorMessage)
                        )
                    }
                }
            )
            Logger.log("[RecommendationsVM] CheckoutHelper.startCheckout completed")
        }
    }
    /**
     * Toggle the account linking dialog visibility
     */
    fun setShowAccountLinking(show: Boolean) {
        _state.update { it.copy(showAccountLinking = show) }
    }

    /**
     * Handle successful account linking
     */
    fun onAccountLinkingSuccess() {
        scope.launch {
            // Update state to close dialog
            _state.update {
                it.copy(showAccountLinking = false)
            }

            // Small delay to ensure merge is complete
            delay(1500)

            // Explicitly refresh auth flags after linking, since linking may not trigger authStateChanged
            try {
                val user = AuthRepository.getCurrentUser()
                if (user != null) {
                    _state.update { it.copy(isAnonymousUser = user.isAnonymous, isNotSignedIn = false) }
                }
            } catch (e: Exception) {
                Logger.error("Failed to refresh auth flags after linking: ${e.message}")
            }

            // Reload the price in case it's different for the authenticated user
            currentQuizId?.let {
                loadUserPrice()
            }
        }
    }

    /**
     * Retry AI generation after error (clears error state and retriggers generation)
     */
    fun retryProsConsGeneration() {
        // Track AI retry click
        AppTracking.logEvent(
            AppTracking.EVENT_RECOMMENDATIONS_AI_RETRY_CLICK,
            mapOf(AppTracking.PARAM_TEMPLATE_ID to 1)
        )

        // Clear the error state
        _state.update {
            it.copy(
                prosConsGenerationError = null,
                isGeneratingProsCons = true
            )
        }

        // Retry generation for current quiz
        currentQuizId?.let { quizId ->
            generateProConsExplanationsIfNeeded(quizId)
        }
    }

    /**
     * Generate AI explanations for pros/cons if they are still question IDs.
     * This is triggered when the quiz becomes unlocked.
     *
     * @param quizId The quiz ID to generate explanations for
     */
    private fun generateProConsExplanationsIfNeeded(quizId: String) {
        scope.launch {
            try {
                val currentRecommendations = _state.value.recommendations

                // Check if any recommendations need generation (have question IDs in the 4 analysis categories)
                val recommendationsNeedingGeneration = currentRecommendations.filter { recommendation ->
                    val hasIds = recommendation.perfectAnswersHigh.any { it.toIntOrNull() != null } ||
                            recommendation.perfectAnswersLow.any { it.toIntOrNull() != null } ||
                            recommendation.differentAnswersHigh.any { it.toIntOrNull() != null } ||
                            recommendation.differentAnswersLow.any { it.toIntOrNull() != null }
                    // Only generate if we have IDs and no generated pros/cons yet
                    hasIds && recommendation.pros.isEmpty() && recommendation.cons.isEmpty()
                }

                if (recommendationsNeedingGeneration.isEmpty()) {
                    Logger.log("No recommendations need pros/cons generation")
                    _state.update { current ->
                        current.copy(isGeneratingProsCons = false)
                    }
                    return@launch
                }

                Logger.log("Generating pros/cons explanations for ${recommendationsNeedingGeneration.size} recommendations in a SINGLE batch request")

                // Track pros/cons generation start
                AppTracking.logEvent(
                    AppTracking.EVENT_RECOMMENDATIONS_PROS_CONS_GENERATE,
                    mapOf(
                        AppTracking.PARAM_TEMPLATE_ID to 1,
                        "specialty_count" to recommendationsNeedingGeneration.size
                    )
                )

                // Mark recommendations as generating
                _state.update {
                    it.copy(
                        isGeneratingProsCons = true,
                        prosConsGenerationError = null
                    )
                }

                // Generate explanations for ALL recommendations in a SINGLE batch request
                val startMs = js("Date.now()") as Double
                val updatedRecommendations = GenerateProConsExplanationsUseCase.executeBatch(currentRecommendations)
                val durationMs = (js("Date.now()") as Double) - startMs

                Logger.log("Generated pros/cons for all specialties in ${durationMs}ms")

                // Use the updated recommendations
                val finalRecommendations = updatedRecommendations

                _state.update {
                    it.copy(
                        recommendations = finalRecommendations,
                        isGeneratingProsCons = false,
                        prosConsGenerationError = null
                    )
                }

                // Save updated recommendations to Firestore
                val updatedResult = _state.value.let { state ->
                    RecommendationsResult(
                        recommendations = state.recommendations,
                        incompatibleRecommendations = state.incompatibleRecommendations
                    )
                }
                UserQuizRepository.setRecommendationsForQuiz(quizId, updatedResult)

                Logger.log("Pros/cons generation completed and saved to Firestore")

                // Track pros/cons generation result
                AppTracking.logEvent(
                    AppTracking.EVENT_RECOMMENDATIONS_PROS_CONS_RESULT,
                    mapOf(
                        AppTracking.PARAM_TEMPLATE_ID to 1,
                        "success" to true,
                        "specialty_count" to recommendationsNeedingGeneration.size,
                        "duration_ms" to durationMs
                    )
                )

            } catch (t: Throwable) {
                Logger.error("Error generating pros/cons explanations: ${t.message}")

                // Set AI generation error with timestamp for cooldown
                val errorTimestamp = js("Date.now()") as Double
                val aiError = ProsConsGenerationError(
                    occurredAt = errorTimestamp.toLong(),
                    message = "Mai durează puțin. Te rugăm să reîncerci curând."
                )
                _state.update { current ->
                    current.copy(
                        prosConsGenerationError = aiError,
                        isGeneratingProsCons = false
                    )
                }

                // Track pros/cons generation failure
                AppTracking.logEvent(
                    AppTracking.EVENT_RECOMMENDATIONS_PROS_CONS_RESULT,
                    mapOf(
                        AppTracking.PARAM_TEMPLATE_ID to 1,
                        "success" to false,
                        "error_message" to (t.message ?: "unknown")
                    )
                )

                AppTracking.logErrorDisplayedEvent(
                    location = "recommendations_generate_proscons",
                    errorType = "proscons_generation_error",
                    quizId = quizId,
                    params = mapOf(
                        "message" to (t.message ?: "unknown")
                    )
                )
            }
        }
    }

    /**
     * Use the locked recommendation ranks saved on the quiz, or generate and save them if there are none.
     *
     * Rules:
     * - Quizzes that already have saved locked ranks keep them as they are (never re-generated or re-saved)
     * - The first displayed card is never locked, so the page always opens with an unlocked card
     * - New quizzes, variant A (ranked): ranks 1 and 2 + 2 random ranks from 4-8, as before the A/B test
     * - New quizzes, variant B: [LOCKED_CARDS_COUNT] random cards, and at most 2 of them can be among the
     *   top [TOP_MATCHES_COUNT] cards by match score
     * - Last 3 incompatible recommendations are always locked but not stored
     *
     * @param quizId The quiz ID
     * @param savedRanks The locked ranks stored on the quiz document, empty if there are none
     * @param recommendations All top recommendations of the quiz
     * @param firstDisplayedRank The rank of the card displayed first, which must stay unlocked
     * @param sorted Whether the ranked variant (A) is shown
     * @return List of locked recommendation ranks
     */
    private suspend fun resolveLockedRanks(
        quizId: String,
        savedRanks: List<Int>,
        recommendations: List<RecommendationItem>,
        firstDisplayedRank: Int?,
        sorted: Boolean
    ): List<Int> {
        if (savedRanks.isNotEmpty()) {
            Logger.log("Loaded existing locked ranks: $savedRanks")
            // Saved ranks are kept as they are, except the first displayed card which must stay unlocked
            return savedRanks.filter { it != firstDisplayedRank }
        }

        val lockedRanks = if (sorted) {
            generateLegacyLockedRanks()
        } else {
            generateLockedRanks(recommendations, firstDisplayedRank)
        }

        Logger.log("Generated new locked ranks: $lockedRanks (variant ${if (sorted) "A" else "B"})")

        // Save to Firestore for consistency across sessions (a failed save is logged by the repository
        // and the generated selection is still used for this visit)
        val saved = UserQuizRepository.setLockedRecommendationRanks(quizId, lockedRanks)
        if (saved) {
            Logger.log("Saved locked ranks to Firestore")
        } else {
            Logger.warn("Failed to save locked ranks to Firestore")
        }

        return lockedRanks
    }

    /**
     * Variant A (previous behavior): ranks 1 and 2 are always locked, plus 2 random ranks from 4-8.
     * Ranks 3, 9 and 10 are always unlocked.
     */
    private fun generateLegacyLockedRanks(): List<Int> = listOf(1, 2) + (4..8).shuffled().take(2)

    /**
     * Pick [LOCKED_CARDS_COUNT] random ranks to lock, never the first displayed card and never
     * a selection made only of the [TOP_MATCHES_COUNT] best matches.
     */
    private fun generateLockedRanks(
        recommendations: List<RecommendationItem>,
        firstDisplayedRank: Int?
    ): List<Int> {
        val candidates = recommendations.map { it.rank }.filter { it != firstDisplayedRank }
        val topMatches = recommendations
            .sortedByDescending { it.matchScore }
            .take(TOP_MATCHES_COUNT)
            .map { it.rank }
            .toSet()

        // Re-draw until the selection isn't entirely the top matches. The check only applies when
        // there are more candidates than locked cards, which guarantees the loop can end.
        var picked: List<Int>
        do {
            picked = candidates.shuffled().take(LOCKED_CARDS_COUNT)
        } while (candidates.size > LOCKED_CARDS_COUNT && topMatches.containsAll(picked))

        return picked
    }

    /**
     * Expose the A/B variant to the UI as soon as it's known, so the header doesn't wait for the recommendations.
     */
    private fun resolveRecommendationsVariant() {
        scope.launch {
            val sorted = recommendationsSorted.await()
            _state.update { it.copy(recommendationsSorted = sorted) }
        }
    }

    /**
     * Track which recommendations variant the user saw, once per page. The user property attributes later
     * events (e.g. purchase) to the variant, and the exposure event is the analysis denominator.
     */
    private fun trackRecommendationsVariantExposure(sorted: Boolean) {
        if (hasTrackedVariantExposure) return
        hasTrackedVariantExposure = true

        val variant = VariantsRepository.recommendationsVariantLabel(sorted)
        Logger.log("[RecommendationsVM] Recommendations variant: $variant (recommendations_sorted=$sorted)")
        AppTracking.setUserProperty(AppTracking.USER_PROP_RECOMMENDATIONS_VARIANT, variant)
        AppTracking.logEvent(
            AppTracking.EVENT_RECOMMENDATIONS_VARIANT_EXPOSURE,
            mapOf(
                AppTracking.PARAM_TEMPLATE_ID to 1,
                AppTracking.PARAM_RECOMMENDATIONS_VARIANT to variant
            )
        )
    }

    private companion object {
        const val LOCKED_CARDS_COUNT = 3
        const val TOP_MATCHES_COUNT = 3
        const val RETRY_DELAY_MS = 1000L
    }
}
